import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getPath, setPath } from "./schemas";
import { ImageField } from "./ImageField";
import { GalleryField } from "./GalleryField";
import { TilesField } from "./TilesField";
import { AiPolish } from "./AiPolish";

const AI_KEYS = { description: true, content: true, body: true, excerpt: false };

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");
const fromLocalInput = (v) => (v ? `${v}:00+10:00` : null);

const Field = ({ f, value, onChange, uploadPath }) => {
  const base = "w-full bg-white border border-[color:var(--line)] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[color:var(--ocean)]";
  const tid = `admin-field-${f.key.replace(".", "-")}`;
  if (f.type === "textarea")
    return <textarea data-testid={tid} rows={f.rows || 4} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "select")
    return (
      <select data-testid={tid} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {f.options.map((o) => <option key={o} value={o}>{f.labels?.[o] || o}</option>)}
      </select>
    );
  if (f.type === "bool")
    return (
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[color:var(--coral)]" />
        Yes
      </label>
    );
  if (f.type === "datetime")
    return <input data-testid={tid} type="datetime-local" className={base} value={toLocalInput(value)} onChange={(e) => onChange(fromLocalInput(e.target.value))} />;
  if (f.type === "number")
    return <input data-testid={tid} type="number" step="any" className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />;
  if (f.type === "tags")
    return <input data-testid={tid} className={base} value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />;
  if (f.type === "image") return <ImageField tid={tid} value={value} onChange={onChange} uploadPath={uploadPath} />;
  if (f.type === "gallery") return <GalleryField tid={tid} value={value} onChange={onChange} uploadPath={uploadPath} />;
  if (f.type === "tiles") return <TilesField tid={tid} value={value} onChange={onChange} uploadPath={uploadPath} />;
  if (f.type === "date")
    return <input data-testid={tid} type="date" className={base} value={value ? value.slice(0, 10) : ""} onChange={(e) => onChange(e.target.value || null)} />;
  return <input data-testid={tid} type={f.type === "url" ? "url" : "text"} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
};

export const AdminForm = ({ section, sectionKey, item, onSaved, onCancel, uploadPath = "/admin/upload", submitFn, submitLabel, aiEnabled = true }) => {
  const [form, setForm] = useState(() => ({ ...section.defaults, ...(item || {}) }));
  const [busy, setBusy] = useState(false);
  const isEdit = !!item?.id;

  const submit = async (e) => {
    e.preventDefault();
    const missing = section.fields.filter((f) => f.required && !getPath(form, f.key) && getPath(form, f.key) !== 0);
    if (missing.length) return toast.error(`Please fill in: ${missing.map((m) => m.label).join(", ")}`);
    setBusy(true);
    try {
      const { data } = submitFn
        ? await submitFn(form)
        : isEdit
        ? await api.put(`/admin/${sectionKey}/${item.id}`, form)
        : await api.post(`/admin/${sectionKey}`, form);
      if (!submitFn) toast.success(isEdit ? "Saved — live site updated." : form.published === false ? "Saved as a hidden draft." : "Published — it's live now.");
      onSaved(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join("; ") : "Couldn't save. Check the fields.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="admin-form" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {section.fields.map((f) => (
          <div key={f.key} className={["textarea", "image", "gallery", "tiles"].includes(f.type) ? "md:col-span-2" : ""}>
            <label className="block text-[0.7rem] uppercase tracking-[0.14em] font-bold text-[color:var(--ink-2)] mb-1.5">
              {f.label}{f.required && <span className="text-[color:var(--coral)]"> *</span>}
            </label>
            <Field f={f} value={getPath(form, f.key)} onChange={(v) => setForm((p) => setPath(p, f.key, v))} uploadPath={uploadPath} />
            <AiPolish
              enabled={aiEnabled && !!AI_KEYS[f.key]}
              text={getPath(form, f.key)}
              kind={section.singular}
              title={form.title || form.name}
              onApply={(v) => setForm((p) => setPath(p, f.key, v))}
              onApplyShort={section.fields.some((x) => x.key === "short_description") ? (v) => setForm((p) => setPath(p, "short_description", v)) : null}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-4 border-t border-dashed border-[color:var(--line)]">
        <button type="submit" disabled={busy} data-testid="admin-form-save-btn" className="btn-pill btn-primary disabled:opacity-50">
          {busy ? "Saving…" : submitLabel || (isEdit ? "Save changes" : `Publish ${section.singular}`)}
        </button>
        <button type="button" onClick={onCancel} data-testid="admin-form-cancel-btn" className="btn-pill btn-ghost">Cancel</button>
      </div>
    </form>
  );
};
